import BottomBar from './BottomBar';

import { useEffect, useMemo, useState } from 'react';
import { AritoFormV2, ConfirmationDialogV2, FormHeaderActionsV2 } from '@/components/custom/erp';
import { createRepeatedTaxRecalculator, useRepeatRowsHandler } from '@/lib/table-handlers';
import { getFormActionButtons, getFormTitle, transformFormData } from '../../utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { calculateTotals, handleUpdateRowFields } from '../../utils/calc-util';
import { formSchema, FormSchema, initialFormValues } from '../../schema';
import { usePhieuNhapHangBanTraLaiSearchContext } from '../../context';
import { useDetailRows, useFormFieldState } from '../../hooks';
import { getDocumentsColumns } from './get-documents-columns';
import { useAuth } from '@/contexts/auth-context';
import InfoDropdown from './InfoDropdown';
import BasicInfoTab from './BasicInfoTab';
import EInvoiceTab from './EInvoiceTab';
import { FormMode } from '@/types/form';
import SummaryTab from './SummaryTab';
import HistoryTab from './HistoryTab';

import GetDocumentsInvoice from '@/components/custom/erp/get-documents-v2';
import DetailTab from './DetailTab';
import OtherTab from './OtherTab';
interface FormDialogProps {
  formMode: FormMode;
  open?: boolean;
  initialData?: BanHangRecord;
  onSubmit?: (data: BanHangPayload) => void;
  onClose: () => void;
  onAdd?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onCopy?: () => void;
  addItem?: (data: BanHangPayload) => Promise<BanHangRecord>;
  updateItem?: (uuid: string, data: BanHangPayload) => Promise<BanHangRecord>;
  patchItem?: (uuid: string, data: BanHangPayload) => Promise<BanHangRecord>;
  handleCloseForm?: () => void;
  clearSelection?: () => void;
  refreshData?: () => void;
}

const recalculateTaxAfterRepeat = createRepeatedTaxRecalculator<BanHangRecord>({
  getTaxBase: row => Number(row?.tien_nt2 || 0),
  roundValue: value => Math.round(value * 100) / 100
});

const FormDialog = ({
  open,
  formMode,
  initialData,
  onClose,
  onSubmit,
  onAdd,
  onEdit,
  onDelete,
  onCopy,
  addItem,
  updateItem,
  patchItem,
  handleCloseForm,
  clearSelection,
  refreshData
}: FormDialogProps) => {
  const { entityUnit } = useAuth();
  const [openDialog, setOpenDialog] = useState('none');
  const [isFormDirty, setIsFormDirty] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('info');
  const {
    rows: detailRows,
    selectedRowUuid: detailSelectedRowUuid,
    selectedCell: detailSelectedCell,
    handleRowClick: detailHandleRowClick,
    handleCellClick: detailHandleCellClick,
    handleAddRow: detailHandleAddRow,
    handleDeleteRow: detailHandleDeleteRow,
    handleCopyRow: detailHandleCopyRow,
    handlePasteRow: detailHandlePasteRow,
    handleMoveRow: detailHandleMoveRow,
    handleCellValueChange: detailHandleCellValueChange,
    setRows: setDetailRows,
    setNonWritableFields: detailSetNonWritableFields,
    setPasteConfigs: detailSetPasteConfigs
  } = useDetailRows(initialData?.chi_tiet_data || [], undefined, handleUpdateRowFields);
  const { state, actions } = useFormFieldState(initialData);
  const { transactionOptions } = usePhieuNhapHangBanTraLaiSearchContext();

  const handleRepeatDetailRows = useRepeatRowsHandler({
    setRows: setDetailRows,
    markDirty: () => setOpenDialog(prev => (prev === 'dirty' ? prev : 'dirty')),
    onAfterRepeat: recalculateTaxAfterRepeat
  });

  const calcTotals = useMemo(() => {
    return calculateTotals(detailRows);
  }, [detailRows]);
  const { tongSoLuong, tongTienNt, tongTienNt2, tongChietKhau, tongThue } = calcTotals;

  useEffect(() => {
    if (!open) {
      setIsFormDirty(false);
      setActiveTab('info');
    }
  }, [open]);

  const normalizeSubmitData = (data: BanHangPayload) => {
    const defaultMaGD =
      formMode === 'add'
        ? transactionOptions[0]?.value || initialFormValues.ma_gd || 'TL'
        : initialData?.ma_gd ||
          initialData?.ma_gd_data?.uuid ||
          transactionOptions[0]?.value ||
          initialFormValues.ma_gd ||
          'TL';

    return {
      ...initialFormValues,
      ...data,
      ma_gd: data.ma_gd || defaultMaGD
    };
  };

  const handleSubmit = async (data: BanHangPayload) => {
    try {
      const submitData = normalizeSubmitData(data);
      const formData = transformFormData(submitData, state, detailRows, entityUnit, calcTotals);

      if (formMode === 'add') {
        await addItem?.(formData);
        if (formData.chi_tiet_items && Array.isArray(formData.chi_tiet_items)) {
          for (const chiTiet of formData.chi_tiet_items) {
            const chiTietHoaDon = detailRows.find((ct: BanHangRecord) => ct.uuid === chiTiet.line_hd);
            if (chiTiet.line_hd) {
              await patchItem?.(chiTiet.line_hd, { sl_cl: chiTietHoaDon?.sl_cl - chiTiet.so_luong });
            }
          }
        }
      } else if (formMode === 'edit' && initialData) {
        await updateItem?.(initialData?.uuid, { ...formData });
        if (formData.chi_tiet_items && Array.isArray(formData.chi_tiet_items)) {
          for (const chiTiet of formData.chi_tiet_items) {
            const chiTietPhieu = initialData?.chi_tiet_data?.find(
              (ct: BanHangRecord) => ct.line_hd?.uuid === chiTiet.line_hd
            );
            const detailRow = detailRows.find((ct: BanHangRecord) => ct.line_hd?.uuid === chiTiet.line_hd);

            if (chiTiet.line_hd) {
              await patchItem?.(chiTiet.line_hd, {
                sl_cl: Number(detailRow?.line_hd?.sl_cl) - Number(chiTiet.so_luong) + Number(chiTietPhieu?.so_luong)
              });
            }
          }
        }
      }
      handleCloseForm?.();
      clearSelection?.();
      refreshData?.();
      setIsFormDirty(false);
    } catch (error) {
      return;
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
  };

  const handleSearchData = (searchedData: BanHangPayload) => {
    const { xprocess, customer, selectedRows } = searchedData;

    // Set customer using actions
    if (customer) {
      actions.setKhachHang(customer);
    }
    const updatedRows = selectedRows.map((row: BanHangRecord) => ({
      ...row,
      tk_thue_no: row.tk_thue_co,
      tk_thue_no_data: row.tk_thue_co_data,
      tk_dt_data: row.ma_vt_data?.tk_tl_data,
      id_hd: row.hoa_don_ban_hang_data,
      so_luong: row.sl_cl,
      line_hd: {
        uuid: row.uuid,
        line: row.line
      }
    }));
    // Handle detail rows with xprocess logic (keep original logic)
    let mapRows = [];
    if (xprocess === '1') mapRows = [...detailRows, ...updatedRows];
    else mapRows = updatedRows;

    setDetailRows(mapRows);
  };

  const title = getFormTitle(formMode, activeTab);
  const actionButtons = getFormActionButtons(formMode, activeTab, {
    onAdd,
    onEdit,
    onDelete,
    onCopy,
    handleClose
  });

  return (
    <>
      <AritoFormV2<FormSchema>
        mode={formMode}
        title={title}
        actionButtons={actionButtons}
        subTitle='Phiếu nhập hàng bán trả lại'
        schema={formSchema}
        initialData={initialData || initialFormValues}
        onSubmit={handleSubmit}
        onClose={onClose}
        headerFields={
          <Tabs value={activeTab} onValueChange={handleTabChange} className='w-full'>
            <div className='flex items-stretch justify-between border-b border-[#d7d7d7] bg-white'>
              <TabsList className='h-8 w-full justify-start rounded-none border-b-0 bg-muted p-0 text-slate-600'>
                <TabsTrigger
                  value='info'
                  className='relative h-8 rounded-none border-b-[3px] border-transparent bg-transparent px-4 py-1 text-[12px] font-semibold text-slate-500 shadow-none ring-0 transition-colors focus-visible:ring-0 data-[state=active]:border-b-main data-[state=active]:bg-main/10 data-[state=active]:text-main data-[state=active]:shadow-none'
                >
                  Thông tin
                </TabsTrigger>
                {formMode === 'view' && (
                  <TabsTrigger
                    value='history'
                    className='relative h-8 rounded-none border-b-[3px] border-transparent bg-transparent px-4 py-1 text-[12px] font-semibold text-slate-500 shadow-none ring-0 transition-colors focus-visible:ring-0 data-[state=active]:border-b-main data-[state=active]:bg-main/10 data-[state=active]:text-main data-[state=active]:shadow-none'
                  >
                    Lịch sử
                  </TabsTrigger>
                )}
              </TabsList>
              <FormHeaderActionsV2
                mode={formMode}
                className='-mr-1'
                source={{
                  label: 'Hóa đơn bán hàng',
                  onClick: () => setOpenDialog('get-documents')
                }}
                info={<InfoDropdown uuid={initialData?.uuid} ma_ct={initialData?.ma_ct} />}
              />
            </div>
            <TabsContent value='info' className='mt-0 outline-none'>
              <div onChange={() => setIsFormDirty(true)}>
                <BasicInfoTab formMode={formMode} formState={{ state, actions }} />
              </div>
            </TabsContent>
            {formMode === 'view' && (
              <TabsContent value='history' className='mt-0 outline-none'>
                <HistoryTab />
              </TabsContent>
            )}
          </Tabs>
        }
        tabs={
          activeTab === 'info' && [
            {
              id: 'details',
              label: 'Chi tiết',
              component: (
                <DetailTab
                  formMode={formMode}
                  rows={detailRows}
                  selectedRowUuid={detailSelectedRowUuid}
                  selectedCell={detailSelectedCell}
                  onRowClick={detailHandleRowClick}
                  onCellClick={detailHandleCellClick}
                  onAddRow={detailHandleAddRow}
                  onDeleteRow={detailHandleDeleteRow}
                  onCopyRow={detailHandleCopyRow}
                  onPasteRow={detailHandlePasteRow}
                  onMoveRow={detailHandleMoveRow}
                  onRepeatForRows={handleRepeatDetailRows}
                  onCellValueChange={detailHandleCellValueChange}
                  handleUpdateNonWritableFields={detailSetNonWritableFields}
                  handleUpdatePasteConfigs={detailSetPasteConfigs}
                  constraints={state.taiKhoan?.constraints ?? state.khachHang?.account_data?.constraints ?? []}
                />
              )
            },
            {
              id: 'e-invoice',
              label: 'HĐĐT',
              component: <EInvoiceTab formMode={formMode} />
            },
            {
              id: 'other',
              label: 'Khác',
              component: <OtherTab formMode={formMode} formState={{ state, actions }} />
            },
            {
              id: 'summary',
              label: 'Tổng cộng',
              component: (
                <SummaryTab
                  formMode={formMode}
                  tongTienNt={tongTienNt}
                  tongTienNt2={tongTienNt2}
                  tongChietKhau={tongChietKhau}
                />
              )
            }
          ]
        }
        bottomBar={
          <BottomBar
            tongSoLuong={tongSoLuong}
            tongTienNt2={tongTienNt2}
            tongChietKhau={tongChietKhau}
            tongThue={tongThue}
          />
        }
      />
      {openDialog === 'get-documents' && (
        <GetDocumentsInvoice
          onSearch={handleSearchData}
          onClose={() => setOpenDialog('none')}
          chungTuType={'HDBH'}
          detailColumns={getDocumentsColumns()}
        />
      )}
    </>
  );
};

export default FormDialog;
